<template>
  <span
    v-if="warning !== null"
    class="reuse-cap-badge ms-1"
    :class="evaluation.status === 'blocked' ? 'reuse-cap--blocked' : 'reuse-cap--partial'"
    v-b-tooltip.hover
    :title="warning"
  >
    <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.89 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
    </svg>
  </span>
</template>

<script lang="ts">
import {computed, defineComponent, onMounted, PropType, ref} from "vue";
import {useStore} from "vuex";
import {helper} from "@/service/CommonHelper";
import TraderRequisitesDTO from "@/models/TraderRequisitesDTO";
import {ReuseMinCapsDTO} from "@/models/ReuseMinCapsDTO";
import {evaluateReuseMinCaps, loadReuseMinCaps} from "@/apps/public/service/reuseMinCaps";

export default defineComponent({
  props: {
    item: {type: Object as PropType<TraderRequisitesDTO>, required: true},
  },
  setup(props) {
    const store = useStore()
    const caps = ref<ReuseMinCapsDTO | null>(null)

    const isApplicable = computed<boolean>(() => {
      return !!store.getters.getAppData?.REQUISITES_REUSE_MIN_SUM_CAP_ENABLED
        && (store.getters.isTrader || store.getters.isTraderOperator)
        && (props.item.maxReuseCount ?? 0) > 0
        && !!props.item.minPayInDealSum
    })

    const evaluation = computed(() => {
      if (!isApplicable.value) {
        return evaluateReuseMinCaps(null, null, null)
      }

      return evaluateReuseMinCaps(caps.value, props.item.currency, Number.parseFloat(props.item.minPayInDealSum!.amount))
    })

    const warning = computed<string | null>(() => {
      if (evaluation.value.status === 'ok') {
        return null
      }

      const lines = [helper.trans(evaluation.value.status === 'blocked' ? 'labels.reuse_cap_blocked' : 'labels.reuse_cap_partial')]
      for (const group of evaluation.value.blockedGroups) {
        lines.push(helper.trans('labels.reuse_cap_group_blocked', {group: group.name ?? helper.trans('labels.reuse_cap_hidden_group'), cap: group.looseCap ?? group.strictCap, currency: props.item.currency}))
      }
      for (const group of evaluation.value.partialGroups) {
        lines.push(helper.trans('labels.reuse_cap_group_partial', {group: group.name ?? helper.trans('labels.reuse_cap_hidden_group'), cap: group.strictCap, currency: props.item.currency}))
      }

      return lines.join('\n')
    })

    onMounted(() => {
      if (!isApplicable.value) {
        return
      }

      loadReuseMinCaps()
        .then((data) => {
          caps.value = data
        })
        .catch(() => {
          caps.value = null
        })
    })

    return {
      evaluation,
      warning,
    }
  },
})
</script>

<style>
.reuse-cap-badge {
  display: inline-flex;
  vertical-align: middle;
  cursor: help;
}

.tooltip-inner {
  white-space: pre-line;
  text-align: left;
}
</style>
